import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  DirectionsCar as LicenseIcon,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { DocumentUpload } from '../DocumentUpload';
import { FileViewerDialog } from '../FileViewerDialog';

interface DetailedDriverLicenseStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  onClose: () => void;
  existingData?: any;
  driver?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const DetailedDriverLicenseStep: React.FC<DetailedDriverLicenseStepProps> = ({
  onNext,
  onBack,
  onClose,
  existingData,
  driver,
  isFirstStep,
  isLastStep
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // State for license records and dialog
  const [licenseRecords, setLicenseRecords] = useState([]);
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);

  // State for license types
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [licenseTypesLoading, setLicenseTypesLoading] = useState(false);

  // State for file viewer
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);

  // Form state for the dialog
  const [licenseForm, setLicenseForm] = useState({
    licenseTypeId: '',
    code: '',
    expiry: '',
    status: 'ACTIVE',
    hasDocument: false,
    documentFileId: null,
    documentFile: null
  });

  // Load license records when driver is available
  useEffect(() => {
    if (driver?.licenses) {
      setLicenseRecords(driver.licenses);
    } else if (driver?.id) {
      loadLicenseRecords();
    }
  }, [driver?.id, driver?.licenses]);

  // Load license types when component mounts
  useEffect(() => {
    const fetchLicenseTypes = async () => {
      setLicenseTypesLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

        const response = await fetch(`${baseUrl}/code-tables/license-types`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch license types`);
        }
        const data = await response.json();
        setLicenseTypes(data);
      } catch (error) {
        console.error('Error fetching license types:', error);
        setLicenseTypes([
          { id: 1, name: 'Class 1 - Tractor Trailer' },
          { id: 2, name: 'Class 2 - Bus' },
          { id: 3, name: 'Class 3 - Straight Truck' },
          { id: 4, name: 'Class 4 - Taxi, Ambulance' },
          { id: 5, name: 'Class 5 - Regular Vehicle' },
        ]);
      } finally {
        setLicenseTypesLoading(false);
      }
    };
    fetchLicenseTypes();
  }, []);

  const loadLicenseRecords = async () => {
    try {
      const result = await dataProvider.getOne('drivers', { id: driver.id });
      if (result.data?.licenses) {
        setLicenseRecords(result.data.licenses);
      }
    } catch (error) {
      console.error('Error loading license records:', error);
      setLicenseRecords([]);
    }
  };

  const handleEditLicense = (license: any) => {
    setEditingLicense(license);
    setLicenseForm({
      licenseTypeId: license.licenseTypeId ? license.licenseTypeId.toString() : '',
      code: license.code || '',
      expiry: license.expiry ? license.expiry.split('T')[0] : '',
      status: license.status || 'ACTIVE',
      hasDocument: !!license.fileId,
      documentFileId: license.fileId || null,
      documentFile: license.file || null
    });
    setLicenseDialogOpen(true);
  };

  const handleDeleteLicense = async (licenseId: number) => {
    if (window.confirm('Are you sure you want to delete this license?')) {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

      try {
        const response = await fetch(`${baseUrl}/drivers/licenses/${licenseId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to delete license`);
        }
        notify('License deleted successfully', { type: 'success' });
        loadLicenseRecords();
        // Refresh the page to ensure data is updated
        window.location.reload();
      } catch (error) {
        console.error('Error deleting license:', error);
        notify('Failed to delete license', { type: 'error' });
      }
    }
  };

  const openFileViewer = (file: any) => {
    setViewingFile(file);
    setFileViewerOpen(true);
  };

  const resetLicenseForm = () => {
    setLicenseForm({
      licenseTypeId: '',
      code: '',
      expiry: '',
      status: 'ACTIVE',
      hasDocument: false,
      documentFileId: null,
      documentFile: null
    });
    setEditingLicense(null);
  };

  const handleSaveLicense = async () => {
    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');
    const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';

    // Validate licenseTypeId
    if (!licenseForm.licenseTypeId) {
      notify('Please select a license type', { type: 'error' });
      return;
    }

    const licenseTypeId = parseInt(licenseForm.licenseTypeId);
    if (isNaN(licenseTypeId)) {
      notify('Invalid license type selected', { type: 'error' });
      return;
    }

    try {
      const licenseData = {
        licenseTypeId: licenseTypeId,
        code: licenseForm.code,
        expiry: licenseForm.expiry || null,
        status: licenseForm.status,
        driverId: driver.id,
        fileId: licenseForm.documentFileId || null
      };

      if (editingLicense) {
        const response = await fetch(`${baseUrl}/drivers/licenses/${editingLicense.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          },
          body: JSON.stringify(licenseData)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to update license`);
        }
        notify('License updated successfully', { type: 'success' });
      } else {
        const response = await fetch(`${baseUrl}/drivers/licenses`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(tenantsEnabled && { 'x-tenant-id': localStorage.getItem('tenantId') })
          },
          body: JSON.stringify(licenseData)
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to create license`);
        }
        notify('License added successfully', { type: 'success' });
      }
      setLicenseDialogOpen(false);
      resetLicenseForm();
      loadLicenseRecords();
      // Refresh the page to ensure data is updated
      window.location.reload();
    } catch (error) {
      console.error('License save error:', error);
      notify(`Failed to ${editingLicense ? 'update' : 'add'} license`, { type: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'normal', mb: 4, textAlign: 'center' }}>
        <LicenseIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Driving Licenses
      </Typography>

      {/* License Records Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">License Records</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              resetLicenseForm();
              setLicenseDialogOpen(true);
            }}
          >
            Add License
          </Button>
        </Box>

        {licenseRecords.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>License Type</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Document</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {licenseRecords.map((license: any) => {
                const isExpired = license.expiry && new Date(license.expiry) < new Date();
                return (
                  <TableRow key={license.id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {license.licenseType?.name || 'Unknown License Type'}
                      </Typography>
                    </TableCell>
                    <TableCell>{license.code || 'N/A'}</TableCell>
                    <TableCell>
                      {license.expiry ? new Date(license.expiry).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isExpired ? 'Expired' : license.status || 'Active'}
                        color={isExpired ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {license.file ? (
                        <Button
                          size="small"
                          onClick={() => openFileViewer(license.file)}
                          variant="outlined"
                        >
                          View Document
                        </Button>
                      ) : (
                        'No document'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditLicense(license)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLicense(license.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No license records found. Click "Add License" to get started.
          </Typography>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 4
      }}>
        {!isFirstStep && (
          <Button
            variant="outlined"
            size="large"
            onClick={onBack}
            sx={{ px: 4, py: 1.5 }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="large"
          onClick={() => onNext({})}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLastStep ? 'Complete' : 'Next'}
        </Button>
      </Box>

      {/* Add/Edit License Dialog */}
      <Dialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLicense ? 'Edit License' : 'Add License'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  License Type
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>License Type</InputLabel>
                  <Select
                    value={licenseForm.licenseTypeId}
                    onChange={(e) => setLicenseForm({ ...licenseForm, licenseTypeId: e.target.value })}
                    label="License Type"
                    disabled={licenseTypesLoading}
                  >
                    {licenseTypesLoading && (
                      <MenuItem disabled>Loading license types...</MenuItem>
                    )}
                    {licenseTypes.map((type: any) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  License Code
                </Typography>
                <MuiTextField
                  fullWidth
                  size="small"
                  value={licenseForm.code}
                  onChange={(e) => setLicenseForm({ ...licenseForm, code: e.target.value })}
                  placeholder="Enter license code"
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Expiry Date
                </Typography>
                <MuiTextField
                  fullWidth
                  type="date"
                  size="small"
                  value={licenseForm.expiry}
                  onChange={(e) => setLicenseForm({ ...licenseForm, expiry: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={licenseForm.status}
                    onChange={(e) => setLicenseForm({ ...licenseForm, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                    <MenuItem value="REVOKED">Revoked</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                  License Document (Optional)
                </Typography>

                {/* Show existing file if editing */}
                {editingLicense && licenseForm.documentFile && (
                  <Box sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => {
                            if (licenseForm.documentFile) {
                              openFileViewer(licenseForm.documentFile);
                            }
                          }}
                        >
                          {licenseForm.documentFile.name || 'License Document'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Current file (click to view)
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setLicenseForm({
                            ...licenseForm,
                            documentFileId: null,
                            documentFile: null,
                            hasDocument: false
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Box>
                )}

                <DocumentUpload
                  documentType="license"
                  driverId={driver?.id}
                  onUploadSuccess={(fileData) => {
                    setLicenseForm({
                      ...licenseForm,
                      documentFileId: fileData.id,
                      documentFile: fileData,
                      hasDocument: true
                    });
                    notify('License document uploaded successfully', { type: 'success' });
                  }}
                  onUploadError={(error) => {
                    notify(`Upload failed: ${error}`, { type: 'error' });
                  }}
                  acceptTypes="image/*,.pdf,.doc,.docx"
                  maxSizeMB={10}
                />
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setLicenseDialogOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          {editingLicense && (
            <Button
              onClick={() => {
                handleDeleteLicense(editingLicense.id);
                setLicenseDialogOpen(false);
                resetLicenseForm();
              }}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ minWidth: 100 }}
            >
              Delete
            </Button>
          )}
          <Button
            disabled={!licenseForm.licenseTypeId}
            onClick={handleSaveLicense}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            {editingLicense ? 'Update' : 'Add'} License
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <FileViewerDialog
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        file={viewingFile}
      />
    </Box>
  );
};